<template>
  <BApp>
    <RouterView />
  </BApp>
</template>

<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useHead } from "@vueuse/head";
import { BApp } from "bootstrap-vue-next";

const route = useRoute();
const baseUrl = import.meta.env.VITE_BASE_URL || "https://chatxbuy.com";
// 與 vite.config.js 的 IS_PROD 同一個判定來源（見該處註解）
const isProd = import.meta.env.VITE_SITE_ENV === "production";

const defaultTitle = "chatXbuy 代買幫 | 1688阿里巴巴批發代購平台";
const defaultDescription =
  "代買幫是專門從1688阿里巴巴上批貨的平台，提供一次性收費、報價後再決定是否要付款、機器人聊天下單以及主動告知貨運進度，只要交給代買幫，讓您輕鬆選購，安心批貨！";
const defaultOgTitle = "chatXbuy 代買幫 | 專業1688批發代購平台";
const defaultOgDescription =
  "聊天機器人一鍵下單，透明報價，全程追蹤。台灣最專業的1688代購服務，讓批貨變簡單！";
const defaultOgImage = "https://i.imgur.com/HousnUg.jpg";
const siteName = "chatXbuy 代買幫";

useHead({
  title: computed(() => route.meta.title || defaultTitle),
  meta: [
    // 測試機禁止搜尋引擎收錄；正式站則由 route.meta.noindex 個別指定（例如 404）
    ...(!isProd
      ? [{ name: "robots", content: "noindex, nofollow" }]
      : [
          {
            name: "robots",
            content: computed(() =>
              route.meta.noindex ? "noindex, follow" : "index, follow",
            ),
          },
        ]),
    {
      name: "description",
      content: computed(() => route.meta.description || defaultDescription),
    },
    // Open Graph
    {
      property: "og:title",
      content: computed(() => route.meta.ogTitle || defaultOgTitle),
    },
    {
      property: "og:description",
      content: computed(() => route.meta.ogDescription || defaultOgDescription),
    },
    {
      property: "og:url",
      content: computed(() => `${baseUrl}${route.path}`),
    },
    {
      property: "og:type",
      content: "website",
    },
    {
      property: "og:image",
      content: defaultOgImage,
    },
    {
      property: "og:locale",
      content: "zh_TW",
    },
    {
      property: "og:site_name",
      content: siteName,
    },
    // Twitter Card
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      name: "twitter:title",
      content: computed(() => route.meta.ogTitle || defaultOgTitle),
    },
    {
      name: "twitter:description",
      content: computed(() => route.meta.ogDescription || defaultOgDescription),
    },
    {
      name: "twitter:image",
      content: defaultOgImage,
    },
  ],
  // noindex 的路由不輸出 canonical：預渲染的 404.html 會被 Firebase 回給所有
  // 不存在的網址，帶著寫死指向 /404 的 canonical 只會誤導爬蟲。
  link: computed(() =>
    route.meta.noindex
      ? []
      : [{ rel: "canonical", href: `${baseUrl}${route.path}` }],
  ),
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "chatXbuy 代買幫",
        url: "https://chatxbuy.com",
        logo: "https://i.imgur.com/HousnUg.jpg",
        description:
          "代買幫是專門從1688阿里巴巴上批貨的平台，提供一次性收費、報價後再決定是否要付款、機器人聊天下單以及主動告知貨運進度。",
        sameAs: ["https://www.facebook.com/chatxbuy"],
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer service",
          availableLanguage: "Chinese",
        },
      }),
    },
  ],
});
</script>
